import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Brain, Mic, FileText, Zap, MessageSquare, Eye, Heart, Users,
  AlertTriangle, BookOpen, Target, TrendingUp, Shield, ChevronDown,
  ChevronUp, ArrowRight, Menu, X, CheckCircle,
} from 'lucide-react';
import { Separator } from '../components/ui/separator';

// ─── Shared fade-up animation ─────────────────────────────────────────────────

function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.55, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ─── Nav ──────────────────────────────────────────────────────────────────────

function Nav() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-lg shadow-md' : 'bg-transparent'}`}>
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-end h-20">
          <div className="hidden md:flex items-center gap-3">
            <button onClick={() => navigate('/login')} className="text-sm font-semibold text-gray-600 hover:text-teal-600 transition-colors px-4 py-2">
              Sign In
            </button>
            <button
              onClick={() => navigate('/coach-registration')}
              className="bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"
            >
              Request Access
            </button>
          </div>
          <button className="md:hidden p-2 text-gray-600" onClick={() => setOpen(o => !o)}>
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 space-y-2">
          <button onClick={() => { setOpen(false); navigate('/login'); }} className="block w-full text-left px-4 py-3 text-sm font-semibold text-gray-600 hover:bg-gray-50 rounded-xl">Sign In</button>
          <button onClick={() => { setOpen(false); navigate('/coach-registration'); }} className="block w-full text-left px-4 py-3 text-sm font-semibold bg-teal-600 text-white rounded-xl">Request Access</button>
        </div>
      )}
    </header>
  );
}

// ─── FAQ accordion ────────────────────────────────────────────────────────────

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between p-5 bg-white hover:bg-gray-50 transition-colors text-left gap-4"
      >
        <span className="text-sm font-semibold text-gray-800">{q}</span>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 bg-white">
          <p className="text-sm text-gray-600 leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
}

// ─── Feature card ─────────────────────────────────────────────────────────────

function FeatureCard({ icon, title, body, example }: { icon: React.ReactNode; title: string; body: string; example?: string }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow">
      <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center text-teal-600 mb-4">
        {icon}
      </div>
      <h3 className="text-base font-bold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-600 leading-relaxed mb-3">{body}</p>
      {example && (
        <div className="bg-gray-50 rounded-lg p-3 border-l-2 border-teal-400">
          <p className="text-xs text-gray-500 italic leading-relaxed">{example}</p>
        </div>
      )}
    </div>
  );
}

// ─── Quote block ──────────────────────────────────────────────────────────────

function ClientQuote({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-amber-50 border-l-4 border-amber-300 rounded-r-xl px-5 py-4 my-2">
      <p className="text-sm text-amber-800 italic leading-relaxed">"{children}"</p>
    </div>
  );
}

// ─── CTA button pair ─────────────────────────────────────────────────────────

function CTAButtons({ className = '' }: { className?: string }) {
  const navigate = useNavigate();
  return (
    <div className={`flex flex-col sm:flex-row gap-3 ${className}`}>
      <button
        onClick={() => navigate('/coach-registration')}
        className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-8 py-3.5 rounded-xl transition-colors text-sm shadow-md hover:shadow-lg"
      >
        Request Access
      </button>
      <button
        onClick={() => navigate('/coach-registration')}
        className="border border-teal-600 text-teal-700 hover:bg-teal-50 font-semibold px-8 py-3.5 rounded-xl transition-colors text-sm"
      >
        Book a Platform Walkthrough
      </button>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const FEATURES = [
  {
    icon: <Brain className="w-5 h-5" />,
    title: 'Limiting Beliefs',
    body: 'Identifies statements where the client may be describing reality through a limiting frame — and surfaces the possible belief behind the language.',
    example: '"I\'m just not confident." → Possible fixed identity frame around confidence as a trait rather than a skill.',
  },
  {
    icon: <Eye className="w-5 h-5" />,
    title: 'NLP Meta Model Patterns',
    body: 'Detects generalisations, deletions, distortions, mind reads, modal operators, cause-effect language, and more — with suggested coach questions for each.',
    example: '"People will judge me." → Mind reading / unspecified reference. Coach question: "Which people specifically? How do you know they will judge you?"',
  },
  {
    icon: <Heart className="w-5 h-5" />,
    title: 'Emotional Language Signals',
    body: 'Highlights emotionally charged language, repeated words, and themes related to fear, shame, guilt, anxiety, overwhelm, resentment, and more.',
    example: 'Client repeatedly uses language around "pressure" and "not being good enough" — possible emotional theme to explore with care.',
  },
  {
    icon: <Users className="w-5 h-5" />,
    title: 'Parts in Conflict',
    body: 'Identifies possible internal tensions where one part of the client wants something while another part resists, avoids, or wants the opposite.',
    example: '"I want to grow, but I hate selling." → Possible parts conflict between ambition and avoidance of judgment.',
  },
  {
    icon: <AlertTriangle className="w-5 h-5" />,
    title: 'Contradictions & Tensions',
    body: 'Surfaces moments where the client\'s stated goals and actual language do not fully align — and suggests how the coach might explore the gap.',
    example: 'Client says they want more clients but also says they don\'t want to post online, follow up, sell, or be seen.',
  },
  {
    icon: <BookOpen className="w-5 h-5" />,
    title: 'Identity-Level Statements',
    body: 'Notices when a client treats a temporary behaviour, skill gap, or state as a fixed identity — and suggests a possible reframe direction.',
    example: '"I\'m just not disciplined." → Treating a habit pattern as a permanent personal trait.',
  },
  {
    icon: <Target className="w-5 h-5" />,
    title: 'Values & Motivation Clues',
    body: 'Highlights what appears to matter most to the client — freedom, security, recognition, impact, belonging — and how it may be shaping their choices.',
    example: 'Client consistently returns to language around "safety" and "not losing what I have" → security as a dominant value clue.',
  },
  {
    icon: <MessageSquare className="w-5 h-5" />,
    title: 'Avoidance & Resistance Patterns',
    body: 'Detects where the client becomes vague, changes subject, laughs things off, intellectualises, or repeatedly says "I don\'t know."',
    example: 'Client gives thorough context for every decision but avoids any question about how they feel about the situation.',
  },
  {
    icon: <Zap className="w-5 h-5" />,
    title: 'Breakthrough Zone',
    body: 'At the end of every report, Session Intelligence™ identifies the single area most likely to produce deep leverage — and suggests a concrete next step for the coach.',
    example: 'Breakthrough Zone: Self-worth and visibility. Reasoning: Every goal the client has is blocked by the same fear of being seen and judged.',
  },
];

const REPORT_SECTIONS = [
  'Session Summary',
  'Key Themes',
  'Limiting Beliefs',
  'Meta Model Patterns',
  'Emotional Language Signals',
  'Parts in Conflict',
  'Contradictions & Tensions',
  'Identity-Level Statements',
  'Values & Motivation Clues',
  'Avoidance & Resistance Patterns',
  'Breakthrough Zone',
  'Suggested Coach Questions (10–15)',
  'Suggested Next Session Focus',
  'Important Cautions',
];

const FAQS = [
  {
    q: 'Does Session Intelligence™ replace the coach?',
    a: 'No. Session Intelligence™ gives the coach a structured second set of eyes on the conversation. The coach remains fully responsible for validating every insight with the client, making judgement calls, and maintaining the human relationship. AI can analyse language. The coach holds the relationship.',
  },
  {
    q: 'Is Session Intelligence™ a diagnostic or therapy tool?',
    a: 'No. Session Intelligence™ does not diagnose the client, label the client, or make absolute claims about their inner world. Every observation is framed as a coaching hypothesis — "This may suggest…", "A possible pattern is…" — for the coach to explore with care and professional judgement.',
  },
  {
    q: 'What formats can I upload?',
    a: 'You can upload audio recordings (MP3, M4A, WAV, OGG — up to 200 MB), documents or transcripts (PDF, Word DOCX, plain text), or analyse from session notes only. Audio is automatically deleted after transcription — only the transcript and report are retained.',
  },
  {
    q: 'Do I need client consent before uploading?',
    a: 'Yes. Before uploading any session recording, transcript, or notes, coaches should ensure they have appropriate permission from the client. Session Intelligence™ should be used with transparency, care, and in line with the data protection and privacy requirements that apply in your location.',
  },
  {
    q: 'Can I discuss the report with an AI thinking partner?',
    a: 'Yes. Once the analysis is complete, you can open the "Discuss with AI" tab to explore the report, bounce hypotheses, plan your next session approach, or get help crafting questions. The AI has full context of the transcript and the analysis report.',
  },
  {
    q: 'Who is Session Intelligence™ designed for?',
    a: 'It is designed for professional coaches, NLP practitioners, hypnotherapists, consultants, leadership coaches, business coaches, and transformation professionals who work with clients in ongoing sessions and want a more structured way to prepare, reflect, and deepen their coaching work.',
  },
];

export function SessionIntelligencePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white font-urbanist">
      <Nav />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="pt-32 pb-20 px-4 md:px-8 bg-gradient-to-b from-teal-700 to-teal-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, white 0%, transparent 60%), radial-gradient(circle at 80% 20%, white 0%, transparent 50%)' }} />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <FadeUp>
            <div className="inline-flex items-center gap-2 bg-white/15 border border-white/25 rounded-full px-4 py-1.5 text-xs font-semibold text-teal-100 mb-6 backdrop-blur-sm">
              <Brain className="w-3.5 h-3.5" />
              Human Potential OS™ — Session Intelligence™
            </div>
          </FadeUp>
          <FadeUp delay={0.08}>
            <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight mb-6">
              See What Your Client's Language<br className="hidden md:block" /> May Be Revealing Beneath the Surface
            </h1>
          </FadeUp>
          <FadeUp delay={0.16}>
            <p className="text-lg text-teal-100 leading-relaxed max-w-2xl mx-auto mb-10">
              AI-assisted coaching session analysis that helps you identify possible limiting beliefs, emotional patterns, contradictions, parts conflicts, Meta Model patterns, and breakthrough opportunities from real coaching conversations.
            </p>
          </FadeUp>
          <FadeUp delay={0.22}>
            <p className="text-base font-semibold text-white mb-8">
              Turn your coaching sessions into clearer insight, sharper next-session preparation, and deeper transformational direction.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={() => navigate('/coach-registration')} className="bg-white text-teal-700 hover:bg-teal-50 font-bold px-8 py-3.5 rounded-xl transition-colors text-sm shadow-lg">
                Request Access
              </button>
              <button onClick={() => navigate('/coach-registration')} className="border border-white/50 text-white hover:bg-white/10 font-semibold px-8 py-3.5 rounded-xl transition-colors text-sm backdrop-blur-sm">
                Book a Platform Walkthrough
              </button>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ── Problem section ───────────────────────────────────────────────── */}
      <section className="py-20 px-4 md:px-8 bg-amber-50">
        <div className="max-w-3xl mx-auto">
          <FadeUp>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center">Most Coaches Rely on Memory After a Session</h2>
            <p className="text-gray-600 leading-relaxed mb-6 text-center">
              A client may speak for 45, 60, or 90 minutes — revealing beliefs, fears, values, contradictions, identity statements, emotional charge, and internal conflicts, often in subtle ways.
            </p>
            <p className="text-gray-600 leading-relaxed mb-6 text-center">But after the session, most coaches are left with their memory, a few notes, intuition, and a general sense of what happened.</p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="bg-white rounded-2xl border border-amber-200 p-8 mt-8">
              <p className="text-sm font-semibold text-gray-500 mb-4 uppercase tracking-wide">A client may say, across one session:</p>
              <div className="space-y-2">
                <ClientQuote>I really want more clients.</ClientQuote>
                <ClientQuote>I hate selling.</ClientQuote>
                <ClientQuote>I don't want people to think I'm pushy.</ClientQuote>
                <ClientQuote>I'm just not confident online.</ClientQuote>
                <ClientQuote>I know what to do, but I don't do it.</ClientQuote>
              </div>
              <div className="mt-6 p-4 bg-teal-50 rounded-xl border border-teal-200">
                <p className="text-sm text-teal-800 font-medium leading-relaxed">
                  To the untrained ear, these may sound like separate comments. But together, they may point to a deeper pattern. That is where Session Intelligence™ helps.
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ── What is it ───────────────────────────────────────────────────── */}
      <section className="py-20 px-4 md:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <FadeUp>
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">What Is Session Intelligence™?</h2>
              <p className="text-gray-600 leading-relaxed max-w-2xl mx-auto">
                Session Intelligence™ is an AI-assisted coaching analysis tool inside Human Potential OS™. Upload a session audio file, transcript, or notes and receive a structured coaching insight report.
              </p>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
            {[
              { icon: <Mic className="w-5 h-5" />, title: 'Audio Recording', sub: 'MP3, M4A, WAV, OGG · up to 200 MB · auto-deleted after transcription' },
              { icon: <FileText className="w-5 h-5" />, title: 'Document / Transcript', sub: 'PDF, Word (.docx), Plain Text — upload existing transcripts' },
              { icon: <MessageSquare className="w-5 h-5" />, title: 'Session Notes', sub: 'Lower confidence analysis from coach notes when no transcript is available' },
            ].map((m, i) => (
              <FadeUp key={i} delay={i * 0.08}>
                <div className="flex flex-col items-center text-center p-6 bg-teal-50 rounded-2xl border border-teal-100">
                  <div className="w-10 h-10 bg-teal-600 rounded-xl flex items-center justify-center text-white mb-3">{m.icon}</div>
                  <p className="text-sm font-bold text-gray-900 mb-1">{m.title}</p>
                  <p className="text-xs text-gray-500 leading-relaxed">{m.sub}</p>
                </div>
              </FadeUp>
            ))}
          </div>

          <FadeUp delay={0.2}>
            <div className="bg-gray-900 rounded-2xl p-8 text-center">
              <Shield className="w-8 h-8 text-teal-400 mx-auto mb-3" />
              <h3 className="text-white font-bold text-lg mb-3">This Is Not Therapy. This Is Not Diagnosis.</h3>
              <p className="text-gray-300 text-sm leading-relaxed max-w-xl mx-auto">
                Session Intelligence™ gives the coach structured coaching hypotheses based on the client's language — not labels, not clinical assessments. The report uses careful language: "This may suggest…", "A possible pattern is…", "The coach may want to explore…". The coach remains responsible for validating every insight with the client.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ── The hidden gold ───────────────────────────────────────────────── */}
      <section className="py-20 px-4 md:px-8 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <FadeUp>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 text-center">The Hidden Gold Is Often in the Client's Language</h2>
            <p className="text-gray-600 text-center mb-10 leading-relaxed">Clients rarely announce their limiting beliefs directly. They reveal them through everyday language.</p>
          </FadeUp>
          <div className="space-y-8">
            {[
              {
                label: 'They may not say they have a limiting belief around visibility. They may say:',
                quotes: ["I don't want to put myself out there.", "People will judge me.", "I'm not really the type of person who promotes myself.", "I know I should post more, but something stops me."],
              },
              {
                label: 'They may not say they have a parts conflict. They may say:',
                quotes: ["Part of me wants to grow, but part of me just wants to stay safe.", "I want success, but I don't want the pressure.", "I want to be seen, but I also want to hide."],
              },
              {
                label: 'They may not say they are operating from a fixed identity frame. They may say:',
                quotes: ["I'm just not confident.", "I'm a procrastinator.", "I'm not a business person.", "I'm too emotional."],
              },
            ].map((block, i) => (
              <FadeUp key={i} delay={i * 0.08}>
                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <p className="text-sm font-semibold text-gray-500 mb-3">{block.label}</p>
                  <div className="space-y-2">
                    {block.quotes.map((q, j) => <ClientQuote key={j}>{q}</ClientQuote>)}
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2}>
            <p className="text-center text-gray-600 mt-10 text-sm leading-relaxed">
              Session Intelligence™ helps the coach notice these patterns, organise them, and prepare better questions for the next session.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ── Features grid ────────────────────────────────────────────────── */}
      <section className="py-20 px-4 md:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <FadeUp>
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">What Session Intelligence™ Looks For</h2>
              <p className="text-gray-600 max-w-xl mx-auto leading-relaxed">Nine layers of coaching signal, each grounded in the client's actual language.</p>
            </div>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <FadeUp key={i} delay={i * 0.05}>
                <FeatureCard {...f} />
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ── Report contents ───────────────────────────────────────────────── */}
      <section className="py-20 px-4 md:px-8 bg-teal-700 text-white">
        <div className="max-w-4xl mx-auto">
          <FadeUp>
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">What You Receive From a Session Intelligence™ Report</h2>
              <p className="text-teal-100 max-w-xl mx-auto leading-relaxed">Every report is structured, section by section, around the client's actual language.</p>
            </div>
          </FadeUp>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {REPORT_SECTIONS.map((s, i) => (
              <FadeUp key={i} delay={i * 0.03}>
                <div className="flex items-center gap-3 bg-white/10 border border-white/20 rounded-xl px-4 py-3 backdrop-blur-sm">
                  <CheckCircle className="w-4 h-4 text-teal-200 flex-shrink-0" />
                  <span className="text-sm font-medium text-white">{s}</span>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why coaches use it ────────────────────────────────────────────── */}
      <section className="py-20 px-4 md:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <FadeUp>
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Why Coaches Use Session Intelligence™</h2>
            </div>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              { title: 'Prepare Better for the Next Session', body: 'Instead of relying only on memory, review a structured analysis before seeing the client again. Know exactly where to focus.' },
              { title: 'Spot Patterns You May Have Missed', body: "The client's language may reveal repeated themes that were not obvious in the moment — visible only when the full session is reviewed together." },
              { title: 'Ask Better Questions', body: 'Receive 10–15 suggested questions based on what the client actually said — grounded in real language, not generic coaching scripts.' },
              { title: 'Deepen Your Coaching Precision', body: 'Move from general coaching direction to specific language-based exploration. Know which thread to pull, and why.' },
              { title: 'Support Your Human Intuition', body: "Session Intelligence™ does not replace intuition. It helps organise and strengthen it — giving structure to what you sensed in the room." },
              { title: 'Create a More Professional Coaching Process', body: 'Clients expect personalisation, structure, and continuity. Session Intelligence™ helps you bring more depth, consistency, and rigour to your work.' },
            ].map((b, i) => (
              <FadeUp key={i} delay={i * 0.06}>
                <div className="flex gap-4 p-6 bg-gray-50 rounded-2xl border border-gray-100 hover:border-teal-200 hover:bg-teal-50/30 transition-colors">
                  <div className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <TrendingUp className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 mb-1">{b.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{b.body}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ── Built for human-led AI ────────────────────────────────────────── */}
      <section className="py-20 px-4 md:px-8 bg-gray-900 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <FadeUp>
            <Brain className="w-10 h-10 text-teal-400 mx-auto mb-6" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">Built for Human-Led, AI-Enhanced Coaching</h2>
            <p className="text-gray-300 leading-relaxed mb-6">
              Human Potential OS™ is built on a simple principle: technology should not replace the human coach. It should enhance the human coach.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left mt-10 mb-10">
              {[
                { label: 'AI can', items: ['Analyse language patterns', 'Identify possible emotional themes', 'Spot repeated contradictions', 'Suggest relevant questions', 'Organise a structured report'] },
                { label: 'You provide', items: ['Human presence and empathy', 'Relationship and trust', 'Professional judgement', 'Validation with the client', 'Transformational skill'] },
              ].map((col, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6">
                  <p className="text-xs font-bold text-teal-300 uppercase tracking-widest mb-4">{col.label}</p>
                  <ul className="space-y-2">
                    {col.items.map((item, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm text-gray-300">
                        <ArrowRight className="w-3.5 h-3.5 text-teal-400 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">
              AI can analyse language. But you hold the human relationship. That is the difference.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ── For / Not for ─────────────────────────────────────────────────── */}
      <section className="py-20 px-4 md:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <FadeUp>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-10 text-center">Who Is This For?</h2>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FadeUp delay={0.06}>
              <div className="bg-teal-50 border border-teal-200 rounded-2xl p-7">
                <h3 className="text-base font-bold text-teal-800 mb-4">This is for you if...</h3>
                <ul className="space-y-3">
                  {[
                    'You want to understand your clients more deeply',
                    'You want to ask better questions',
                    'You want to spot limiting beliefs more quickly',
                    'You want to notice patterns across sessions',
                    'You want to prepare more intelligently before each session',
                    'You want to bring AI into your practice without losing the human element',
                    'You want to create a more structured, professional, and modern coaching experience',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-teal-800">
                      <CheckCircle className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeUp>
            <FadeUp delay={0.12}>
              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-7">
                <h3 className="text-base font-bold text-gray-700 mb-4">This is not for you if...</h3>
                <ul className="space-y-3">
                  {[
                    'You want AI to diagnose your clients',
                    'You want AI to replace your coaching skill',
                    'You do not want to use client consent and ethical data handling',
                    'You are looking for a therapy or mental health assessment tool',
                    'You want generic AI summaries instead of structured coaching insight',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <X className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-gray-500 mt-5 leading-relaxed">Session Intelligence™ is designed for professional coaches who want to use AI responsibly to support better human coaching.</p>
              </div>
            </FadeUp>
          </div>

          <FadeUp delay={0.16}>
            <div className="mt-8 bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="flex gap-3">
                <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-amber-800 mb-1">Important Privacy and Consent Note</p>
                  <p className="text-sm text-amber-700 leading-relaxed">
                    Before uploading any audio, transcript, or session notes, coaches should ensure they have permission from their client to record, transcribe, and use the material for AI-assisted coaching support. Session Intelligence™ should be used with transparency, care, and professional judgement, following the privacy and data protection requirements that apply in your location.
                  </p>
                </div>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ── New standard ─────────────────────────────────────────────────── */}
      <section className="py-20 px-4 md:px-8 bg-gray-50">
        <div className="max-w-3xl mx-auto text-center">
          <FadeUp>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">A New Standard for Coaching Reflection</h2>
            <p className="text-gray-600 leading-relaxed mb-8">
              Instead of asking <em>"What do I remember from that session?"</em> — you can ask better questions.
            </p>
          </FadeUp>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left mb-10">
            {[
              "What did the client's language reveal?",
              'What patterns repeated?',
              'What emotional themes showed up?',
              'What beliefs may be shaping behaviour?',
              'What contradictions are worth exploring?',
              'What is the next best coaching question?',
            ].map((q, i) => (
              <FadeUp key={i} delay={i * 0.04}>
                <div className="flex items-start gap-2.5 bg-white border border-gray-100 rounded-xl px-4 py-3">
                  <ArrowRight className="w-4 h-4 text-teal-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-gray-700 italic">{q}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2}>
            <p className="text-gray-600 text-sm leading-relaxed">
              When preparation improves, the next session can become more focused, more precise, and more transformational.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ── Part of HPOS ─────────────────────────────────────────────────── */}
      <section className="py-16 px-4 md:px-8 bg-white border-y border-gray-100">
        <div className="max-w-3xl mx-auto text-center">
          <FadeUp>
            <img src="/human_potential_os_logo_V2_cropped.png" alt="Human Potential OS" className="h-12 mx-auto mb-6 opacity-80" />
            <p className="text-sm text-gray-500 leading-relaxed max-w-xl mx-auto">
              Session Intelligence™ is one part of <strong className="text-gray-700">Human Potential OS™</strong> — a wider coaching operating system designed to help coaches attract clients, understand the person, identify patterns, support progress, assign pathways, provide human presence, demonstrate proof, and build a more sustainable coaching business.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section className="py-20 px-4 md:px-8 bg-gray-50">
        <div className="max-w-2xl mx-auto">
          <FadeUp>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-10 text-center">Frequently Asked Questions</h2>
          </FadeUp>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <FadeUp key={i} delay={i * 0.04}>
                <FAQItem q={faq.q} a={faq.a} />
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ────────────────────────────────────────────────────── */}
      <section className="py-24 px-4 md:px-8 bg-teal-700 text-white text-center">
        <div className="max-w-2xl mx-auto">
          <FadeUp>
            <Zap className="w-10 h-10 text-teal-200 mx-auto mb-6" />
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">Turn Client Conversations Into Coaching Intelligence</h2>
            <p className="text-teal-100 leading-relaxed mb-10">
              Your client's language may already contain the clues. Session Intelligence™ helps you organise those clues into clearer insight, better questions, and deeper coaching direction.
            </p>
            <CTAButtons className="justify-center" />
          </FadeUp>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <footer className="py-12 px-4 md:px-8 bg-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <img src="/human_potential_os_logo_V2_cropped.png" alt="Human Potential OS" className="h-10 opacity-70" />
            <div className="flex gap-6 text-sm text-gray-500">
              <button onClick={() => navigate('/privacy')} className="hover:text-gray-300 transition-colors">Privacy Policy</button>
              <button onClick={() => navigate('/terms')} className="hover:text-gray-300 transition-colors">Terms of Service</button>
              <button onClick={() => navigate('/disclaimer')} className="hover:text-gray-300 transition-colors">Disclaimer</button>
            </div>
          </div>
          <Separator className="bg-gray-700 my-6" />
          <p className="text-center text-xs text-gray-600">Copyright 2025 Human Potential OS, All Rights Reserved.</p>
        </div>
      </footer>
    </div>
  );
}
